/**
 * TTRPGReportsView — dumb half of the post-session TTRPG reports surface.
 *
 * Renders the scene gallery (sectioned: scenes, memorable moments, live
 * session), DM performance report, per-player roleplay reports, and per-
 * character arc cards. All data + the lightbox modal arrive as props from
 * the smart wrapper at ../index.tsx; this file owns nothing but local UI
 * state (modals, disclosures) and rendering.
 *
 * Theming note: every status / rating colour is a Mantine semantic palette
 * (`success`, `warning`, `info`, `brand`, `highlight`, `danger`, `gray`)
 * — never a literal hue ("green", "teal", "violet"). The CSS module mirrors
 * this with `--mantine-color-success-1` etc., so brand swaps under
 * `[data-product]` propagate without component edits.
 */
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { Link } from "@tanstack/react-router"
import { useProduct } from "../../../lib/tenant"
import type { ICharacterArc, IDMReport, IPlayerReport, IVisual } from "../interfaces"
import styles from "../styles.module.css"
import type { IViewProps, TVisibility } from "./interfaces"

const RATING_CLASSES: Record<string, string> = {
  great: styles.ratingGreat,
  good: styles.ratingGood,
  solid: styles.ratingSolid,
  needs_work: styles.ratingNeedsWork,
  excellent: styles.ratingGreat,
  developing: styles.ratingSolid,
  quiet: styles.ratingNeedsWork,
}

/**
 * Map relationship-change verbs onto Mantine's semantic palettes so the
 * "tells the story at a glance" intent survives a brand swap. `new` is
 * informational (FYI, not yet judged), `improved` is a positive outcome,
 * `strained` is destructive/negative, `unchanged` is neutral.
 */
const RELATIONSHIP_COLORS: Record<string, string> = {
  new: "info",
  improved: "success",
  strained: "danger",
  unchanged: "gray",
}

function ratingClass(rating: string): string {
  return RATING_CLASSES[rating] ?? styles.ratingSolid
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface IGalleryCardProps {
  visual: IVisual
  isDM: boolean
  onImageClick: () => void
  revealPending: boolean
  onReveal: (visibility: TVisibility) => void
}

function GalleryCard({ visual: v, isDM, onImageClick, revealPending, onReveal }: IGalleryCardProps): React.JSX.Element {
  const { i18n } = useLingui()
  const [inspectOpened, { open: openInspect, close: closeInspect }] = useDisclosure(false)
  const isHidden = v.visibility === "hidden"
  const isRevealed = v.visibility === "all"
  const label = v.content.title ?? v.content.prompt ?? i18n._(msg`Scene`)
  const refs = v.content.referenceImages ?? []

  return (
    <>
      <div className={`${styles.imageCard} ${isHidden ? styles.hiddenImage : ""}`}>
        <div className={styles.imageWrapper}>
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: image lightbox, ESC handled by Modal */}
          <img
            src={v.content.url}
            alt={label}
            className={styles.image}
            loading="lazy"
            style={{ cursor: "zoom-in" }}
            onClick={onImageClick}
          />
          {isDM && (
            <div className={styles.imageOverlay}>
              {isHidden ? null : isRevealed ? (
                <ActionIcon
                  size="xs"
                  variant="filled"
                  color="gray"
                  title={i18n._(msg`Hide from players`)}
                  loading={revealPending}
                  onClick={() => onReveal("dm_only")}
                >
                  👁
                </ActionIcon>
              ) : (
                <ActionIcon
                  size="xs"
                  variant="filled"
                  color="success"
                  title={i18n._(msg`Reveal to players`)}
                  loading={revealPending}
                  onClick={() => onReveal("all")}
                >
                  ✦
                </ActionIcon>
              )}
            </div>
          )}
          {isDM && (
            <Badge
              size="xs"
              color={isRevealed ? "success" : isHidden ? "gray" : "warning"}
              style={{ position: "absolute", top: 4, right: 4 }}
            >
              {isRevealed ? i18n._(msg`revealed`) : isHidden ? i18n._(msg`hidden`) : i18n._(msg`DM only`)}
            </Badge>
          )}
        </div>

        <Box className={styles.imageCaption}>
          {v.content.description && (
            <Text size="xs" c="dimmed" fs="italic" lineClamp={2} mb={4}>
              {v.content.description}
            </Text>
          )}
          <Button size="compact-xs" variant="subtle" color="dimmed" onClick={openInspect}>
            <Trans>Inspect</Trans>
          </Button>
        </Box>
      </div>

      <Modal
        opened={inspectOpened}
        onClose={closeInspect}
        title={v.content.title ?? i18n._(msg`Scene details`)}
        size="lg"
      >
        <Stack gap="md">
          {v.content.description && (
            <Box>
              <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
                <Trans>Story excerpt</Trans>
              </Text>
              <Text size="sm" fs="italic">
                {v.content.description}
              </Text>
            </Box>
          )}

          {v.content.prompt && (
            <Box>
              <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
                <Trans>Image prompt</Trans>
              </Text>
              <Text size="sm" c="dimmed">
                {v.content.prompt}
              </Text>
            </Box>
          )}

          {refs.length > 0 && (
            <Box>
              <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={8}>
                <Trans>Reference images used</Trans>
              </Text>
              <Group gap="sm" wrap="wrap">
                {refs.map((url) => (
                  <img key={url} src={url} alt={i18n._(msg`Character reference`)} className={styles.refThumb} />
                ))}
              </Group>
            </Box>
          )}

          {v.content.provider && (
            <Text size="xs" c="dimmed">
              <Trans>Generated via {v.content.provider}</Trans>
            </Text>
          )}
        </Stack>
      </Modal>
    </>
  )
}

interface IVisualSectionProps {
  title: string
  visuals: IVisual[]
  imageOffset: number
  isDM: boolean
  onImageClick: (index: number) => void
  revealPending: (id: number) => boolean
  onReveal: (id: number, visibility: TVisibility) => void
}

function VisualSection({
  title,
  visuals,
  imageOffset,
  isDM,
  onImageClick,
  revealPending,
  onReveal,
}: IVisualSectionProps): React.JSX.Element | null {
  if (visuals.length === 0) return null
  return (
    <Box mb="md">
      <Text fw={600} size="sm" mb="sm" c="dimmed" tt="uppercase" style={{ fontSize: 11 }}>
        {title}
      </Text>
      <div className={styles.gallery}>
        {visuals.map((v, i) => (
          <GalleryCard
            key={v.id}
            visual={v}
            isDM={isDM}
            onImageClick={() => onImageClick(imageOffset + i)}
            revealPending={revealPending(v.id)}
            onReveal={(vis) => onReveal(v.id, vis)}
          />
        ))}
      </div>
    </Box>
  )
}

interface IDMReportCardProps {
  report: IDMReport
}

function DMReportCard({ report }: IDMReportCardProps): React.JSX.Element {
  return (
    <div className={styles.reportCard}>
      <Group mb="sm">
        <Text fw={600} size="sm">
          <Trans>DM Performance Report</Trans>
        </Text>
        <span
          className={`${styles.ratingGreat} ${ratingClass(report.overall_rating)}`}
          style={{ padding: "2px 8px", borderRadius: 4, fontSize: 12 }}
        >
          {report.overall_rating.replace("_", " ")}
        </span>
      </Group>

      <Stack gap="xs">
        <div>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={2}>
            <Trans>Pacing</Trans>
          </Text>
          <Text size="sm">{report.pacing}</Text>
        </div>

        <div>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={2}>
            <Trans>Storytelling</Trans>
          </Text>
          <Text size="sm">{report.storytelling}</Text>
        </div>

        <div>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={2}>
            <Trans>Spotlight Balance</Trans>
          </Text>
          <Text size="sm">
            <Trans>
              Most active: <strong>{report.engagement_balance.most_active}</strong> · Least active:{" "}
              <strong>{report.engagement_balance.least_active}</strong>
            </Trans>
          </Text>
          <Text size="xs" c="dimmed">
            {report.engagement_balance.note}
          </Text>
        </div>

        <Divider />

        <div>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
            <Trans>Strengths</Trans>
          </Text>
          <Stack gap={2}>
            {report.strengths.map((s) => (
              <div key={s} className={styles.listItem}>
                <span className={styles.listDot}>✓</span>
                <Text size="sm">{s}</Text>
              </div>
            ))}
          </Stack>
        </div>

        <div>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
            <Trans>Suggestions for next session</Trans>
          </Text>
          <Stack gap={2}>
            {report.suggestions.map((s) => (
              <div key={s} className={styles.listItem}>
                <span className={styles.listDot}>→</span>
                <Text size="sm">{s}</Text>
              </div>
            ))}
          </Stack>
        </div>
      </Stack>
    </div>
  )
}

interface IPlayerReportCardProps {
  report: IPlayerReport
  characterName?: string
}

function PlayerReportCard({ report, characterName }: IPlayerReportCardProps): React.JSX.Element {
  const { i18n } = useLingui()
  const label = characterName ?? i18n._(msg`Player ${report.userId.slice(0, 6)}`)
  return (
    <div className={styles.reportCard}>
      <Group mb="sm">
        <Text fw={600} size="sm">
          {label}
        </Text>
        <span
          className={ratingClass(report.roleplay_rating)}
          style={{ padding: "2px 8px", borderRadius: 4, fontSize: 12 }}
        >
          {report.roleplay_rating}
        </span>
      </Group>

      <Stack gap="xs">
        <div>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
            <Trans>Highlights</Trans>
          </Text>
          <Stack gap={2}>
            {report.highlights.map((h) => (
              <div key={h} className={styles.listItem}>
                <span className={styles.listDot}>✓</span>
                <Text size="sm">{h}</Text>
              </div>
            ))}
          </Stack>
        </div>

        {report.missed_opportunities.length > 0 && (
          <div>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
              <Trans>Missed opportunities</Trans>
            </Text>
            <Stack gap={2}>
              {report.missed_opportunities.map((m) => (
                <div key={m} className={styles.listItem}>
                  <span className={styles.listDot}>◦</span>
                  <Text size="sm">{m}</Text>
                </div>
              ))}
            </Stack>
          </div>
        )}

        <div>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={2}>
            <Trans>Character consistency</Trans>
          </Text>
          <Text size="sm">{report.character_consistency}</Text>
        </div>

        <div>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
            <Trans>Tips for next session</Trans>
          </Text>
          <Stack gap={2}>
            {report.suggestions.map((s) => (
              <div key={s} className={styles.listItem}>
                <span className={styles.listDot}>→</span>
                <Text size="sm">{s}</Text>
              </div>
            ))}
          </Stack>
        </div>
      </Stack>
    </div>
  )
}

interface ICharacterArcCardProps {
  arc: ICharacterArc
  portrait?: IVisual
  onPortraitClick?: () => void
}

function CharacterArcCard({ arc, portrait, onPortraitClick }: ICharacterArcCardProps): React.JSX.Element {
  return (
    <div className={`${styles.reportCard} ${styles.arcCard}`}>
      {portrait && (
        // biome-ignore lint/a11y/useKeyWithClickEvents: lightbox ESC handled by Modal
        <img
          src={portrait.content.url}
          alt={arc.characterName}
          className={styles.characterPortrait}
          onClick={onPortraitClick}
          style={{ cursor: "zoom-in" }}
        />
      )}

      <Text fw={600} size="sm" mb="xs">
        {arc.characterName}
      </Text>

      <Stack gap="xs">
        <div>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={2}>
            <Trans>This session</Trans>
          </Text>
          <Text size="sm">{arc.arc_progression}</Text>
        </div>

        {arc.growth_moments.length > 0 && (
          <div>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
              <Trans>Growth moments</Trans>
            </Text>
            <Stack gap={2}>
              {arc.growth_moments.map((g) => (
                <div key={g} className={styles.listItem}>
                  <span className={styles.listDot}>★</span>
                  <Text size="sm">{g}</Text>
                </div>
              ))}
            </Stack>
          </div>
        )}

        {arc.relationships.length > 0 && (
          <div>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
              <Trans>Relationships</Trans>
            </Text>
            <Group gap="xs">
              {arc.relationships.map((r) => (
                <Badge
                  key={r.with}
                  size="xs"
                  color={RELATIONSHIP_COLORS[r.change] ?? "gray"}
                  className={styles.relationshipBadge}
                >
                  {r.with}: {r.change}
                </Badge>
              ))}
            </Group>
          </div>
        )}

        <div>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={2}>
            <Trans>Campaign arc so far</Trans>
          </Text>
          <Text size="sm" fs="italic" c="dimmed">
            {arc.cumulative_summary}
          </Text>
        </div>
      </Stack>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main TTRPGReportsView
// ---------------------------------------------------------------------------

export function TTRPGReportsView({
  meetingId,
  isDM,
  sceneVisuals,
  momentVisuals,
  characterVisuals,
  otherVisuals,
  dmReport,
  playerReports,
  characterArcs,
  totalImages,
  onImageClick,
  momentOffset,
  characterOffset,
  otherOffset,
  isLoading,
  revealPending,
  onReveal,
  lightboxModal,
}: IViewProps): React.JSX.Element | null {
  const { i18n } = useLingui()
  const product = useProduct()

  const hasContent =
    sceneVisuals.length + momentVisuals.length + characterVisuals.length + otherVisuals.length > 0 ||
    dmReport !== null ||
    playerReports.length > 0 ||
    characterArcs.length > 0

  if (isLoading) {
    return <Loader size="sm" mt="xl" />
  }

  if (!hasContent) return null

  return (
    <Stack gap={0} mt="xl">
      <Divider mb="xl" />

      <Group justify="space-between" mb="md">
        <Title order={4}>
          <Trans>Session Summary</Trans>
        </Title>
        {product === "voxcanon" ? (
          <Button
            renderRoot={(props) => <Link to="/meetings/$id/viewer" params={{ id: meetingId }} {...props} />}
            variant="light"
            size="xs"
            className={styles.viewerLink}
          >
            <Trans>Open viewer screen ↗</Trans>
          </Button>
        ) : null}
      </Group>

      {/* Scene Gallery — sectioned */}
      {totalImages > 0 && (
        <Paper withBorder p="md" mb="md">
          <div className={styles.sectionTitle}>
            <Text fw={600} size="sm">
              <Trans>Scene Gallery</Trans>
            </Text>
            <Badge size="xs" variant="light">
              <Trans>
                {totalImages} image{totalImages !== 1 ? "s" : ""}
              </Trans>
            </Badge>
            {isDM && (
              <Text size="xs" c="dimmed">
                <Trans>· Click ✦ to reveal to players, 👁 to hide</Trans>
              </Text>
            )}
          </div>

          <VisualSection
            title={i18n._(msg`Scenes`)}
            visuals={sceneVisuals}
            imageOffset={0}
            isDM={isDM}
            onImageClick={onImageClick}
            revealPending={revealPending}
            onReveal={onReveal}
          />
          <VisualSection
            title={i18n._(msg`Memorable moments`)}
            visuals={momentVisuals}
            imageOffset={momentOffset}
            isDM={isDM}
            onImageClick={onImageClick}
            revealPending={revealPending}
            onReveal={onReveal}
          />
          {/* Other (progressive-visual, on_demand, etc.) */}
          <VisualSection
            title={i18n._(msg`Live session`)}
            visuals={otherVisuals}
            imageOffset={otherOffset}
            isDM={isDM}
            onImageClick={onImageClick}
            revealPending={revealPending}
            onReveal={onReveal}
          />
        </Paper>
      )}

      {/* DM Report */}
      {dmReport && (
        <Paper withBorder p="md" mb="md">
          <DMReportCard report={dmReport} />
        </Paper>
      )}

      {/* Player Reports */}
      {playerReports.length > 0 && (
        <Paper withBorder p="md" mb="md">
          <div className={styles.sectionTitle}>
            <Text fw={600} size="sm">
              <Trans>Player Reports</Trans>
            </Text>
          </div>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            {playerReports.map((r) => {
              const arc = characterArcs.find((a) => a.userId === r.userId)
              return <PlayerReportCard key={r.userId} report={r} characterName={arc?.characterName} />
            })}
          </SimpleGrid>
        </Paper>
      )}

      {/* Character Arcs — with portrait if available */}
      {characterArcs.length > 0 && (
        <Paper withBorder p="md" mb="md">
          <div className={styles.sectionTitle}>
            <Text fw={600} size="sm">
              <Trans>Character Arcs</Trans>
            </Text>
          </div>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            {characterArcs.map((arc) => {
              const portrait = characterVisuals.find(
                (v) => v.content.characterName?.toLowerCase() === arc.characterName?.toLowerCase(),
              )
              const portraitIndex = portrait ? characterOffset + characterVisuals.indexOf(portrait) : -1
              return (
                <CharacterArcCard
                  key={arc.characterName}
                  arc={arc}
                  portrait={portrait}
                  onPortraitClick={portrait ? () => onImageClick(portraitIndex) : undefined}
                />
              )
            })}
          </SimpleGrid>
        </Paper>
      )}

      {lightboxModal}
    </Stack>
  )
}
